import {
  advertisingAccountActivityResponseSchema,
  advertisingAccountsResponseSchema,
  advertisingPaymentProfileResponseSchema,
  advertisingQuoteRequestResponseSchema,
  advertisingQuoteResultResponseSchema,
  advertisingRechargeOptionsResponseSchema,
  advertisingRechargeRequestResponseSchema,
  billingOverviewResponseSchema,
  billingProductsResponseSchema,
  billingTransactionsResponseSchema,
  billingOrderResponseSchema,
  billingOrdersResponseSchema,
  mockPurchaseResponseSchema,
  mockPurchasePreviewSchema,
  type BillingProductType,
} from "@/features/billing/billingSchemas";
import { baseApi } from "@/shared/api/baseApi";

interface BillingProductsQuery {
  organizationId: string;
  type?: BillingProductType;
}

export interface BillingTransactionsQuery {
  cursor?: string;
  limit?: number;
  organizationId: string;
  fromDate?: string;
  toDate?: string;
  timezone?: string;
  sourceType?: string;
}

interface MockPurchaseQuery {
  organizationId: string;
  productId: string;
  expectedRevision: string;
  amountMinor?: string;
  idempotencyKey: string;
}

interface AdvertisingAccountQuery {
  adAccountId: string;
  organizationId: string;
}

interface AdvertisingPaymentProfileQuery extends AdvertisingAccountQuery {
  profileId: string;
}

interface AdvertisingActivityQuery extends AdvertisingAccountQuery {
  month?: string;
  search?: string;
  sourceType?: string;
}

interface CreateAdvertisingQuoteRequestQuery {
  adAccountId: string;
  amountMinor: string;
  currencyCode: string;
  idempotencyKey: string;
  organizationId: string;
  paymentProfileId: string;
}

interface AdvertisingQuoteResultQuery {
  organizationId: string;
  quoteRequestId: string;
}

interface CreateAdvertisingRechargeRequestQuery {
  attachmentResourceIds: string[];
  idempotencyKey: string;
  note: string;
  organizationId: string;
  quoteId: string;
  remittedAt: string;
}

export const billingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBillingOverview: builder.query<
      ReturnType<typeof billingOverviewResponseSchema.parse>,
      string
    >({
      providesTags: (_result, _error, organizationId) => [
        { id: organizationId, type: "BillingOverview" },
      ],
      query: (organizationId) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/overview`,
      transformResponse: (response) =>
        billingOverviewResponseSchema.parse(response),
    }),
    getBillingProducts: builder.query<
      ReturnType<typeof billingProductsResponseSchema.parse>,
      BillingProductsQuery
    >({
      providesTags: (_result, _error, query) => [
        { id: query.type ?? "all", type: "BillingProducts" },
      ],
      query: ({ organizationId, type }) => ({
        params: type ? { type } : undefined,
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/products`,
      }),
      transformResponse: (response) =>
        billingProductsResponseSchema.parse(response),
    }),
    getBillingTransactions: builder.query<
      ReturnType<typeof billingTransactionsResponseSchema.parse>,
      BillingTransactionsQuery
    >({
      providesTags: (_result, _error, query) => [
        { id: query.organizationId, type: "BillingTransactions" },
      ],
      query: ({
        cursor,
        limit = 5,
        organizationId,
        fromDate,
        toDate,
        timezone,
        sourceType,
      }) => ({
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
          ...(fromDate ? { from_date: fromDate } : {}),
          ...(toDate ? { to_date: toDate } : {}),
          ...(timezone ? { timezone } : {}),
          ...(sourceType ? { source_type: sourceType } : {}),
        },
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/transactions`,
      }),
      transformResponse: (response) =>
        billingTransactionsResponseSchema.parse(response),
    }),
    getBillingOrders: builder.query<
      ReturnType<typeof billingOrdersResponseSchema.parse>,
      { organizationId: string; cursor?: string }
    >({
      query: ({ organizationId, cursor }) => ({
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/orders`,
        params: { limit: 20, ...(cursor ? { cursor } : {}) },
      }),
      transformResponse: (response) =>
        billingOrdersResponseSchema.parse(response),
      providesTags: (_r, _e, q) => [
        { id: q.organizationId, type: "BillingOrder" },
      ],
    }),
    getBillingOrder: builder.query<
      ReturnType<typeof billingOrderResponseSchema.parse>,
      { organizationId: string; orderId: string }
    >({
      query: ({ organizationId, orderId }) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/orders/${encodeURIComponent(orderId)}`,
      transformResponse: (response) =>
        billingOrderResponseSchema.parse(response),
      providesTags: (_r, _e, q) => [
        { id: q.organizationId, type: "BillingOrder" },
      ],
    }),
    getMockPurchasePreview: builder.query<
      ReturnType<typeof mockPurchasePreviewSchema.parse>,
      Omit<MockPurchaseQuery, "idempotencyKey">
    >({
      query: ({
        organizationId,
        productId,
        expectedRevision,
        amountMinor,
      }) => ({
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/mock-purchase-preview`,
        params: {
          product_id: productId,
          expected_revision: expectedRevision,
          ...(amountMinor ? { amount_minor: amountMinor } : {}),
        },
      }),
      transformResponse: (response) =>
        mockPurchasePreviewSchema.parse(response),
    }),
    createMockPurchase: builder.mutation<
      ReturnType<typeof mockPurchaseResponseSchema.parse>,
      MockPurchaseQuery
    >({
      query: ({
        organizationId,
        productId,
        expectedRevision,
        amountMinor,
        idempotencyKey,
      }) => ({
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/mock-purchases`,
        method: "POST",
        body: {
          product_id: productId,
          expected_revision: expectedRevision,
          idempotency_key: idempotencyKey,
          ...(amountMinor ? { amount_minor: amountMinor } : {}),
        },
      }),
      transformResponse: (response) =>
        mockPurchaseResponseSchema.parse(response),
      invalidatesTags: (result, _error, q) =>
        result
          ? [
              { id: q.organizationId, type: "BillingOverview" },
              { id: q.organizationId, type: "BillingTransactions" },
              { id: q.organizationId, type: "BillingOrder" },
              { id: q.organizationId, type: "ResourceAvailability" },
              "CloudResources",
            ]
          : [],
    }),
    getAdvertisingAccounts: builder.query<
      ReturnType<typeof advertisingAccountsResponseSchema.parse>,
      string
    >({
      providesTags: (_result, _error, organizationId) => [
        { id: organizationId, type: "AdvertisingAccounts" },
      ],
      query: (organizationId) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/accounts`,
      transformResponse: (response) =>
        advertisingAccountsResponseSchema.parse(response),
    }),
    getAdvertisingRechargeOptions: builder.query<
      ReturnType<typeof advertisingRechargeOptionsResponseSchema.parse>,
      AdvertisingAccountQuery
    >({
      providesTags: (_result, _error, query) => [
        { id: query.adAccountId, type: "AdvertisingRechargeOptions" },
      ],
      query: ({ adAccountId, organizationId }) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/accounts/${encodeURIComponent(adAccountId)}/recharge-options`,
      transformResponse: (response) =>
        advertisingRechargeOptionsResponseSchema.parse(response),
    }),
    getAdvertisingPaymentProfile: builder.query<
      ReturnType<typeof advertisingPaymentProfileResponseSchema.parse>,
      AdvertisingPaymentProfileQuery
    >({
      query: ({ adAccountId, organizationId, profileId }) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/accounts/${encodeURIComponent(adAccountId)}/payment-profiles/${encodeURIComponent(profileId)}`,
      transformResponse: (response) =>
        advertisingPaymentProfileResponseSchema.parse(response),
    }),
    getAdvertisingAccountActivity: builder.query<
      ReturnType<typeof advertisingAccountActivityResponseSchema.parse>,
      AdvertisingActivityQuery
    >({
      providesTags: (_result, _error, query) => [
        { id: query.adAccountId, type: "AdvertisingAccountActivity" },
      ],
      query: ({ adAccountId, month, organizationId, search, sourceType }) => ({
        params: {
          ...(month ? { month } : {}),
          ...(search ? { search } : {}),
          ...(sourceType ? { source_type: sourceType } : {}),
        },
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/accounts/${encodeURIComponent(adAccountId)}/activity`,
      }),
      transformResponse: (response) =>
        advertisingAccountActivityResponseSchema.parse(response),
    }),
    createAdvertisingQuoteRequest: builder.mutation<
      ReturnType<typeof advertisingQuoteRequestResponseSchema.parse>,
      CreateAdvertisingQuoteRequestQuery
    >({
      invalidatesTags: (_result, _error, query) => [
        { id: query.organizationId, type: "AdvertisingQuote" },
      ],
      query: ({ organizationId, ...input }) => ({
        body: {
          ad_account_id: input.adAccountId,
          amount_minor: input.amountMinor,
          currency_code: input.currencyCode,
          idempotency_key: input.idempotencyKey,
          payment_profile_id: input.paymentProfileId,
        },
        method: "POST",
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/quote-requests`,
      }),
      transformResponse: (response) =>
        advertisingQuoteRequestResponseSchema.parse(response),
    }),
    getAdvertisingQuoteResult: builder.query<
      ReturnType<typeof advertisingQuoteResultResponseSchema.parse>,
      AdvertisingQuoteResultQuery
    >({
      providesTags: (_result, _error, query) => [
        { id: query.quoteRequestId, type: "AdvertisingQuote" },
      ],
      query: ({ organizationId, quoteRequestId }) =>
        `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/quote-requests/${encodeURIComponent(quoteRequestId)}`,
      transformResponse: (response) =>
        advertisingQuoteResultResponseSchema.parse(response),
    }),
    createAdvertisingRechargeRequest: builder.mutation<
      ReturnType<typeof advertisingRechargeRequestResponseSchema.parse>,
      CreateAdvertisingRechargeRequestQuery
    >({
      invalidatesTags: (_result, _error, query) => [
        { id: query.organizationId, type: "AdvertisingAccounts" },
      ],
      query: ({ organizationId, ...input }) => ({
        body: {
          attachment_resource_ids: input.attachmentResourceIds,
          idempotency_key: input.idempotencyKey,
          note: input.note,
          quote_id: input.quoteId,
          remitted_at: input.remittedAt,
        },
        method: "POST",
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/billing/advertising/recharge-requests`,
      }),
      transformResponse: (response) =>
        advertisingRechargeRequestResponseSchema.parse(response),
    }),
  }),
});

export const {
  useGetBillingOverviewQuery,
  useGetBillingProductsQuery,
  useGetBillingTransactionsQuery,
  useLazyGetBillingTransactionsQuery,
  useCreateMockPurchaseMutation,
  useGetMockPurchasePreviewQuery,
  useGetBillingOrdersQuery,
  useGetBillingOrderQuery,
  useCreateAdvertisingQuoteRequestMutation,
  useCreateAdvertisingRechargeRequestMutation,
  useGetAdvertisingAccountActivityQuery,
  useGetAdvertisingAccountsQuery,
  useGetAdvertisingPaymentProfileQuery,
  useGetAdvertisingQuoteResultQuery,
  useGetAdvertisingRechargeOptionsQuery,
} = billingApi;
